// ── @paramms/chat-widget/hooks — React bindings for the headless SDK ─────────
// Build your own chat UI with two hooks. Same primitives as the bundled
// widget and the agent dashboard — these are bindings, not a reimplementation.
//
//   // ONE url, any scheme — 'https://api.relay.paramms.com' works everywhere.
//   const { messages, send, status, typing } = useRelayConversation({
//     url, token, profileId: 'p_x', subjectId: 'listing_42',
//   })
//   const dm = useRelayConversation({ url, token, profileId: 'p_x', kind: 'direct', peerId: 'user_bob' })
//   const { conversations } = useRelayChatList({ url, token, profileId: 'p_x' })
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { RelayClient, RelayConversation, type RelayClientOptions, type OpenOptions } from './core.js'
import { persistentUid } from './uid.js'
import { resolveRelayUrls } from './history.js'
import type { RenderMessage } from './store.js'

export type UseRelayConversationOptions = RelayClientOptions & OpenOptions

export interface RelayConversationHook {
  /** Messages in seq order (optimistic sends included, pending-marked). */
  messages: RenderMessage[]
  send: (text: string) => void
  /** Call from your input's onChange for typing indicators. */
  typing: (isTyping: boolean) => void
  markRead: () => void
  /** connecting | connected | reconnecting | error */
  status: string
  statusMessage: string | undefined
  conversationId: string | undefined
  /** Our canonical user id (server-resolved) — compare with message.senderId for "mine". */
  me: string | undefined
  /** userIds currently typing (excluding you). */
  peersTyping: string[]
  /** The underlying conversation for anything the hook doesn't surface
   *  (reactions, invoke, raw store). */
  conversation: RelayConversation
}

/** One live conversation (support thread or DM). The connection opens on
 *  mount and closes on unmount; options changes reopen. */
export function useRelayConversation(opts: UseRelayConversationOptions): RelayConversationHook {
  // Key that captures every option that requires a NEW conversation/socket.
  const key = `${opts.url}|${opts.token ?? ''}|${opts.profileId}|${opts.subjectId ?? ''}|${opts.kind ?? ''}|${opts.peerId ?? ''}`
  const convoRef = useRef<{ key: string; convo: RelayConversation } | null>(null)
  if (!convoRef.current || convoRef.current.key !== key) {
    convoRef.current?.convo.close()
    convoRef.current = { key, convo: new RelayClient(opts).open(opts) }
  }
  const convo = convoRef.current.convo

  // Close on unmount (and on Strict-Mode double-invoke, reopen lazily above).
  useEffect(() => () => { convoRef.current?.convo.close(); convoRef.current = null }, [])

  // Subscribe React to the store via useSyncExternalStore: `version` bumps on
  // every change, and the snapshot getters below memo off it. (This is the
  // lesson from the dashboard's stale-useMemo bug baked into the API.)
  const versionRef = useRef(0)
  const version = useSyncExternalStore(
    (onStoreChange) => convo.onChange(() => { versionRef.current++; onStoreChange() }),
    () => versionRef.current,
    () => versionRef.current,
  )

  const messages = useMemo(() => convo.store.messages(), [convo, version])
  const peersTyping = useMemo(() => [...convo.store.typing], [convo, version])

  return {
    messages,
    send: (text: string) => convo.send(text),
    typing: (isTyping: boolean) => convo.typing(isTyping),
    markRead: () => convo.markRead(),
    status: convo.status,
    statusMessage: convo.statusMessage,
    conversationId: convo.conversationId as string | undefined,
    me: convo.me as string | undefined,
    peersTyping,
    conversation: convo,
  }
}

export interface ChatListRow {
  conversationId: string
  /** Which chatroom this conversation belongs to. With `scope: 'tenant'` rows
   *  can come from OTHER chatrooms of the same business — open them with this
   *  profileId, not the one you queried with. */
  profileId?: string
  /** 'support' (default) or 'direct' (user↔user DM). */
  kind?: string
  /** For direct conversations: the other participant's user id. */
  peerId?: string
  subjectId?: string
  title?: string
  lastMessage?: string
  updatedAt: number
  unread?: boolean
}

/** The current user's conversation list (the "inbox" screen of a chat app).
 *  Polls the REST endpoint; pass `refreshMs: 0` to fetch once.
 *
 *  `scope` (default `'profile'`) controls which conversations are listed:
 *    • `'profile'` — only this chatroom's threads (the classic widget list).
 *    • `'tenant'`  — every conversation this user has with the OWNING BUSINESS
 *      of the given profileId, across all of its chatrooms — what a real
 *      chat-app inbox shows. Rows carry `profileId` so you can open each one
 *      against the right chatroom. (The profileId is still required: it is
 *      how the server resolves which tenant "mine" means — guests never see
 *      or send tenant ids.) */
export function useRelayChatList(opts: Omit<RelayClientOptions, 'profileId'> & {
  /** A chatroom id — or omit and pass `tenantId` for tenant-level listing. */
  profileId?: string
  /** Tenant-level identification: list EVERY conversation this user has with
   *  the business across ALL of its chatrooms, no profileId needed (e.g. a
   *  platform with a marketplace chatroom AND a general-support chatroom).
   *  Provide `profileId` OR `tenantId`. */
  tenantId?: string
  refreshMs?: number
  scope?: 'profile' | 'tenant'
}): {
  conversations: ChatListRow[]
  loading: boolean
  error: string | null
  refresh: () => void
  /** Where a "new conversation" should open: the configured profileId, else
   *  the server-reported tenant default (oldest chatroom). */
  defaultProfileId: string | undefined
} {
  const [conversations, setConversations] = useState<ChatListRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)
  const [serverDefault, setServerDefault] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!opts.profileId && !opts.tenantId) { setError('provide profileId or tenantId'); setLoading(false); return }
    let cancelled = false
    const { httpBase } = resolveRelayUrls(opts.url, opts.apiUrl)
    // Same identity tiers as everywhere else: token > (in a browser) the
    // persistent anonymous id. Previously this defaulted to '' — anonymous
    // visitors got a 401 and the list never loaded.
    const token = opts.token ?? (typeof window !== 'undefined' ? persistentUid() : '')
    async function load(): Promise<void> {
      try {
        const qs = opts.profileId
          ? `profileId=${encodeURIComponent(opts.profileId)}${opts.scope === 'tenant' ? '&scope=tenant' : ''}`
          : `tenantId=${encodeURIComponent(opts.tenantId!)}`
        const res = await fetch(`${httpBase}/conversations/mine?${qs}`, {
          headers: token ? { authorization: `Bearer ${token}` } : {},
        })
        if (!res.ok) throw new Error(`chat list failed: ${res.status}`)
        const data = await res.json() as { conversations: Array<{ id: string; profileId?: string; kind?: string; peerId?: string; subjectId?: string; subjectTitle?: string; lastMessage?: string; updatedAt: number; unread?: boolean }>; defaultProfileId?: string }
        if (data.defaultProfileId) setServerDefault(data.defaultProfileId)
        if (cancelled) return
        setConversations(data.conversations.map(c => ({
          conversationId: c.id,
          ...(c.profileId ? { profileId: c.profileId } : {}),
          ...(c.kind      ? { kind:      c.kind }      : {}),
          ...(c.peerId    ? { peerId:    c.peerId }    : {}),
          ...(c.subjectId ? { subjectId: c.subjectId } : {}),
          ...(c.subjectTitle ? { title: c.subjectTitle } : {}),
          ...(c.lastMessage ? { lastMessage: c.lastMessage } : {}),
          updatedAt: c.updatedAt,
          ...(c.unread !== undefined ? { unread: c.unread } : {}),
        })))
        setError(null)
      } catch (e) {
        if (!cancelled) setError((e as Error).message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    const ms = opts.refreshMs ?? 15_000
    const timer = ms > 0 ? setInterval(() => void load(), ms) : null
    return () => { cancelled = true; if (timer) clearInterval(timer) }
  }, [opts.url, opts.apiUrl, opts.token, opts.profileId, opts.tenantId, opts.refreshMs, opts.scope, nonce])

  return {
    conversations, loading, error,
    refresh: () => setNonce(n => n + 1),
    defaultProfileId: opts.profileId ?? serverDefault,
  }
}
